import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import {
  Search as SearchIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import axios from "../../config/axios";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

function Admin() {
  const navigate = useNavigate();
  const [admin, setAdmin] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [adminToDelete, setAdminToDelete] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  const fetchAdmin = useCallback(async () => {
    try {
      const response = await axios.get("/admin", {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          search,
        },
      });
      setAdmin(response.data.admin);
    } catch (error) {
      console.error("Error fetching admin:", error);
      toast.error(error.response?.data?.message || "Error fetching admin");
    }
  }, [page, rowsPerPage, search]);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get("/auth/me");
      if (response.data && response.data.role) {
        setCurrentUser(response.data);
      } else {
        console.error("Invalid user data received:", response.data);
      }
    } catch (error) {
      console.error("Error fetching current user:", error);
      toast.error(
        error.response?.data?.message || "Error fetching current user data"
      );
    }
  };

  useEffect(() => {
    fetchAdmin();
    fetchCurrentUser();
  }, [fetchAdmin]);

  // Debug log for current user
  useEffect(() => {}, [currentUser]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = (event) => {
    setSearch(event.target.value);
    setPage(0);
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await axios.delete(`/admin/${adminToDelete.id}`);
      setDeleteDialogOpen(false);
      setAdminToDelete(null);
      toast.success(response.data.message);
      fetchAdmin();
    } catch (error) {
      console.error("Error deleting admin:", error);
      toast.error(error.response?.data?.message || "Error deleting admin");
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setAdminToDelete(null);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        p: 3,
        animation: "fadeIn 0.8s ease-in-out",
        "@keyframes fadeIn": {
          "0%": { opacity: 0, transform: "translateY(-20px)" },
          "100%": { opacity: 1, transform: "translateY(0)" },
        },
        ...(window.innerWidth <= 600 && { p: 1 }), // Reduced padding on mobile
        ...(window.innerWidth <= 960 && { p: 2 }), // Adjusted padding for tablets
      }}
      style={{
        width: "100%",
        boxSizing: "border-box",
        "@media (max-width: 960px)": {
          padding: "16px",
        },
        "@media (max-width: 600px)": {
          padding: "8px",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
          "@media (max-width: 600px)": {
            flexDirection: "column",
            alignItems: "flex-start",
            gap: "16px",
          },
        }}
      >
        <Typography
          variant="h1"
          sx={{
            color: "#26A69A",
            fontSize: "32px",
            fontWeight: 600,
            "@media (max-width: 960px)": { fontSize: "24px" },
            "@media (max-width: 600px)": { fontSize: "20px" },
          }}
        >
          Admins
        </Typography>
        <Button
          onClick={() => navigate("/admin/add")}
          sx={{
            background: "#26A69A",
            color: "#FFFFFF",
            padding: "10px 20px",
            borderRadius: "8px",
            fontWeight: 500,
            textTransform: "none",
            transition: "all 0.3s",
            "&:hover": { background: "#009688", transform: "scale(1.02)" },
            "@media (max-width: 600px)": {
              width: "100%",
              padding: "8px 16px",
              fontSize: "14px",
            },
          }}
        >
          Add Admin
        </Button>
      </Box>

      {/* {error && (
        <Alert
          severity="error"
          sx={{
            mb: 3,
            "& .MuiAlert-message": {
              display: "flex",
              alignItems: "center",
            },
            "@media (max-width: 600px)": {
              fontSize: "0.9rem",
              padding: "8px",
            },
          }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )} */}

      <TextField
        fullWidth
        variant="outlined"
        placeholder="Search admin..."
        value={search}
        onChange={handleSearch}
        sx={{
          mb: 3,
          "& .MuiOutlinedInput-root": {
            borderRadius: "8px",
            background: "rgba(255, 255, 255, 0.8)",
            "&:hover fieldset": {
              borderColor: "#26A69A",
            },
            "&.Mui-focused fieldset": {
              borderColor: "#26A69A",
            },
            "@media (max-width: 600px)": {
              fontSize: "0.9rem",
              padding: "6px",
            },
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
          endAdornment: search ? (
            <InputAdornment position="end">
              <IconButton onClick={() => setSearch("")} edge="end">
                <CloseIcon />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
      />

      <TableContainer
        component={Paper}
        sx={{
          background: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(10px)",
          borderRadius: "16px",
          transition: "transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out",
          "@media (max-width: 960px)": {
            overflowX: "auto", // Enable horizontal scrolling on tablets
          },
          "@media (max-width: 600px)": {
            overflowX: "auto", // Enable horizontal scrolling on mobile
            fontSize: "0.9rem",
          },
        }}
        style={{
          width: "100%",
          "@media (max-width: 960px)": {
            minWidth: "600px", // Ensure table is scrollable
          },
          "@media (max-width: 600px)": {
            minWidth: "400px", // Adjusted for mobile
          },
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: "bold",
                  color: "#26A69A",
                  "@media (max-width: 600px)": {
                    padding: "4px",
                    fontSize: "0.9rem",
                  },
                }}
              >
                Email
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: "bold",
                  color: "#26A69A",
                  "@media (max-width: 600px)": {
                    padding: "4px",
                    fontSize: "0.9rem",
                  },
                }}
              >
                Role
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: "bold",
                  color: "#26A69A",
                  "@media (max-width: 600px)": {
                    padding: "4px",
                    fontSize: "0.9rem",
                  },
                }}
              >
                Status
              </TableCell>
              {currentUser?.role === "super_admin" && (
                <TableCell
                  sx={{
                    fontWeight: "bold",
                    color: "#26A69A",
                    "@media (max-width: 600px)": {
                      padding: "4px",
                      fontSize: "0.9rem",
                    },
                  }}
                >
                  Actions
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {admin.map((adminItem, index) => (
              <TableRow
                key={adminItem.id}
                sx={{
                  backgroundColor:
                    index % 2 === 0
                      ? "rgba(255, 255, 255, 0.1)"
                      : "transparent",
                  transition: "background-color 0.3s",
                  "&:hover": {
                    backgroundColor: "#E0F2F1",
                  },
                  "@media (max-width: 600px)": {
                    "& td": {
                      padding: "4px",
                      fontSize: "0.9rem",
                    },
                  },
                }}
              >
                <TableCell
                  sx={{
                    fontSize: "1rem",
                    "@media (max-width: 600px)": { fontSize: "0.9rem" },
                  }}
                >
                  {adminItem.email}
                </TableCell>
                <TableCell
                  sx={{
                    fontSize: "1rem",
                    "@media (max-width: 600px)": { fontSize: "0.9rem" },
                  }}
                >
                  {adminItem.role.charAt(0).toUpperCase() +
                    adminItem.role.slice(1)}
                </TableCell>
                <TableCell
                  sx={{
                    fontSize: "1rem",
                    "@media (max-width: 600px)": { fontSize: "0.9rem" },
                  }}
                >
                  {adminItem.status.charAt(0).toUpperCase() +
                    adminItem.status.slice(1)}
                </TableCell>
                <TableCell>
                  {currentUser?.role === "super_admin" && (
                    <IconButton
                      onClick={() => navigate(`/admin/edit/${adminItem.id}`)}
                      disabled={adminItem.role === "super_admin"}
                      style={{
                        "@media (max-width: 600px)": { padding: "4px" },
                      }}
                    >
                      <EditIcon style={{ color: "#263238" }} />
                    </IconButton>
                  )}
                  {currentUser?.role === "super_admin" && (
                    <IconButton
                      onClick={() => {
                        setAdminToDelete(adminItem);
                        setDeleteDialogOpen(true);
                      }}
                      disabled={adminItem.role === "super_admin"}
                      style={{
                        "@media (max-width: 600px)": { padding: "4px" },
                      }}
                    >
                      <DeleteIcon style={{ color: "#FF6F61" }} />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={admin.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            "@media (max-width: 600px)": {
              "& .MuiTablePagination-selectLabel": { fontSize: "0.9rem" },
              "& .MuiTablePagination-displayedRows": { fontSize: "0.9rem" },
              "& .MuiTablePagination-select": { fontSize: "0.9rem" },
              "& .MuiIconButton-root": { padding: "4px" },
            },
          }}
        />
      </TableContainer>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            padding: "16px",
            "@media (max-width: 600px)": {
              width: "90%",
              margin: "0 auto",
            },
            "@media (max-width: 960px)": {
              width: "80%",
              margin: "0 auto",
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            color: "#1e40af",
            fontWeight: "bold",
            "@media (max-width: 600px)": {
              fontSize: "1.2rem",
            },
          }}
        >
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography
            sx={{
              "@media (max-width: 600px)": {
                fontSize: "0.9rem",
              },
            }}
          >
            Are you sure you want to delete the admin account for{" "}
            <b>{adminToDelete?.email}</b>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            padding: "16px",
            "@media (max-width: 600px)": { padding: "8px" },
          }}
        >
          <Button
            onClick={handleDeleteCancel}
            sx={{
              background: "#FFFFFF",
              color: "#263238",
              padding: "10px 20px",
              borderRadius: "8px",
              border: "2px solid #263238",
              fontWeight: 500,
              textTransform: "none",
              transition: "all 0.3s",
              "&:hover": {
                background: "#263238",
                color: "#FFFFFF",
                transform: "scale(1.02)",
              },
              "@media (max-width: 600px)": {
                padding: "6px 12px",
                fontSize: "0.9rem",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            sx={{
              background: "#FF6F61",
              color: "#FFFFFF",
              padding: "10px 20px",
              borderRadius: "8px",
              fontWeight: 500,
              textTransform: "none",
              transition: "all 0.3s",
              "&:hover": { background: "#F4511E", transform: "scale(1.02)" },
              "@media (max-width: 600px)": {
                padding: "6px 12px",
                fontSize: "0.9rem",
              },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Admin;
